import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import {
  useFaqCategoriesQuery,
  useFaqCategoryMutation,
} from '../hooks/useFaqCategoriesQuery';
import type { FaqCategory, FaqCategoryPayload } from '../types/faq';
import { FaqCategoryFormModal } from '../components/FaqCategoryFormModal';

interface FormModalState {
  open: boolean;
  mode: 'create' | 'edit';
  editTarget: FaqCategory | null;
}

const INITIAL_FORM: FormModalState = { open: false, mode: 'create', editTarget: null };

export const FaqCategoryListPage = () => {
  const { t } = useTranslation('common');
  const query = useFaqCategoriesQuery();
  const { create, update, remove } = useFaqCategoryMutation();

  const [formModal, setFormModal] = useState<FormModalState>(INITIAL_FORM);
  const [deleteTarget, setDeleteTarget] = useState<FaqCategory | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openCreate = () => setFormModal({ open: true, mode: 'create', editTarget: null });
  const openEdit = (category: FaqCategory) => setFormModal({ open: true, mode: 'edit', editTarget: category });
  const closeForm = () => setFormModal(INITIAL_FORM);

  const handleFormSubmit = (payload: FaqCategoryPayload) => {
    if (formModal.mode === 'create') {
      create.mutate(payload, { onSuccess: closeForm });
    } else if (formModal.editTarget) {
      update.mutate({ categoryId: formModal.editTarget.id, payload }, { onSuccess: closeForm });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    remove.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (e) =>
        setDeleteError(
          e instanceof Error && e.message === 'CONFLICT'
            ? t('faq.categories.deleteConflict')
            : t('faq.categories.deleteFailed'),
        ),
    });
  };

  const items = query.data ?? [];
  const formPending = create.isPending || update.isPending;

  const columns: DataTableColumn<FaqCategory>[] = [
    {
      key: 'name',
      header: t('faq.categories.cols.name'),
      cell: (row) => (
        <span className="block text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {row.name}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('faq.categories.cols.sortOrder'),
      align: 'right',
      width: 96,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('faq.categories.cols.isActive'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'info'}
          label={row.isActive ? t('faq.active') : t('faq.inactive')}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => (
        <RowActions
          triggerLabel={t('faq.categories.rowActionsLabel')}
          actions={[
            { key: 'edit', label: t('faq.edit'), onClick: () => openEdit(row) },
            {
              key: 'delete',
              label: t('faq.delete'),
              variant: 'danger',
              separatorBefore: true,
              onClick: () => { setDeleteError(null); setDeleteTarget(row); },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageShell
      title={t('faq.categories.title')}
      actions={
        <Button size="sm" onClick={openCreate}>
          <Plus size={14} aria-hidden="true" />
          {t('faq.categories.create')}
        </Button>
      }
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('faq.categories.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('faq.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          emptyState={t('faq.categories.empty')}
          stickyHeader
        />
      )}

      <FaqCategoryFormModal
        open={formModal.open}
        onOpenChange={(open) => { if (!open) closeForm(); }}
        mode={formModal.mode}
        editTarget={formModal.editTarget}
        isPending={formPending}
        onSubmit={handleFormSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(null); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('faq.categories.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('faq.categories.deleteDesc', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <p className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>{deleteError}</p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">{t('faq.cancel')}</Button>
            </DialogClose>
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={handleDeleteConfirm}
            >
              {t('faq.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
